// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

// definition of an enline parameter set, i.e. the complete configuration of one site.
//
// conventions valid for the whole parameter set:
// - every list item carries a persistent "id" (IParameterItem.id); if it is missing (legacy
//   sets), the array index within its list is used instead. ids are unique per list only,
//   must stay stable and are the target of all cross references between items.
// - a numeric member referencing another item holds that item's id; null means "not set".
//   the comments below name the list the id refers to.
// - units: time in seconds, power in kW, energy in kWh, current in A unless noted otherwise.
// - condition blocks may still carry the legacy binary "itsSettings" blob instead of the
//   explicit json fields. new/changed blocks should set the json fields only; if both exist,
//   the json fields win. do not invent itsSettings content.

// -------------------- common item basics --------------------

// where an input/output/energy meter is physically read or written
export enum Bus {
  LC = "LC", // legacy LON
  FT = "FT", // legacy LON
  IP = "IP", // legacy
  LOCAL = "Local", // io of the controller itself
  MOD = "Modbus RTU", // legacy
  M = "M-Bus", // legacy
  VIRTUAL = "Virtual", // no hardware; value written via api, modbus server or cloud
  CHARGER = "Charger", // provided by a charger of the charger list
  MODULE = "Module", // provided by a module of the module list
}

export interface IAddress {
  bus: Bus;
  // id of the providing item: module id for Bus.MODULE, charger id for Bus.CHARGER;
  // freely assigned identifier for Bus.VIRTUAL, by which the item is written; unused on Bus.LOCAL
  module: number;
  // channel within that module/charger: the index within the input/output/energymeter
  // channel list of its type, as defined in modules/enplanconfig.json
  channel: number;
}

// base of all parameter list items (inputs, outputs, conditions, ...)
export interface IParameterItem {
  // the id is used to address the item, i.e. at modules
  // if omitted (legacy), the index of this element within the container-array is used
  id?: number;
  name: string;
  isEnabled: boolean;
  webActive: boolean; // show item in the web ui
  webCtrl: boolean; // allow manual control of the item in the web ui
  mqttActive: boolean; // publish item on mqtt
  comment?: string;
}

// -------------------- inputs --------------------

export enum InputType {
  SWITCH = 0, // digital state
  PUSHBUTTON_OLD = 1, // not used anymore, only up to enline 4.1
  ANALOG = 2, // numeric value, scaled by factor/offset
  PUSHBUTTON = 3, // digital, toggles its value on each rising edge
  PULSE = 4, // counts pulses
}

// InputUnit was numeric in enline <= 8.0-124 and changed to string type then
// the special units are interpreted as time values, e.g. by the time reference block
export type InputUnit = "date" | "time" | "timestamp" | string | undefined;

export type IInput = IParameterItem &
  IAddress & {
    type: InputType;
    isInverted: boolean; // digital types only
    unit: InputUnit;
    fallback?: number; // no/undefined fallback is possible
    categories?: string[]; // free tags, used for grouping in the ui and reports

    // scaling, analog only: value = raw * factor + offset
    offset: number;
    factor: number;

    // trending, i.e. recording of the value; all zero disables trending
    hysteresis: number; // record on change of value by more than this
    interval: number; // s, record at least this often
    minInterval?: number; // s, trending minimum interval; introduced in enline 7.4
  };

// -------------------- outputs --------------------

export enum OutputMode {
  CLOSE = 0, // digital, follows the condition
  OPEN = 1, // digital, inverted
  PULSE_ON = 2, // pulse of pulseTime on the rising edge
  PULSE_OFF = 3, // pulse on the falling edge
  PULSE_BOTH = 4, // pulse on any edge
  ANALOG = 5, // takes the analog condition value
  PWM = 6, // pulse width modulation, duty cycle = condition value (0..1)
}

// manual override commands, not part of the parameter set
export enum OutputCmd {
  LINX = 0, // no override, controlled by the condition
  OVERRIDE_ON = 1,
  OVERRIDE_OFF = 2,
  OFF = 3,
  ON = 4,
  TOGGLE = 5,
}

export enum OutputFallbackState {
  ON = 0,
  OFF = 1,
  LAST = 2, // keep last value
}

export type IOutput = IParameterItem &
  IAddress & {
    mode: OutputMode;
    power: number; // kW, switchable load, used by the load management of the regulation
    unit?: string; // introduced in v24.04
    fallback: OutputFallbackState | number; // enum on digital; numeric on analog outputs
    categories?: string[];

    // controlling item
    condition: number | null; // id of the condition providing the value
    regulation: number | null; // id of the regulation this load takes part in

    // switching timing
    pulseTime: number; // ms, may be set to 100 (default) on old parameters even without pulsing
    cycleTime: number; // s, min. time between switching events; pwm period in mode PWM

    // trending; all zero disables trending
    hysteresis?: number; // trending hysteresis; introduced in enline 7.4
    interval?: number; // s, trending maximum interval; introduced in enline 7.4
    minInterval?: number; // s, trending minimum interval; introduced in enline 7.4
    averaging?: number; // s, off if zero or undefined
  };

// -------------------- conditions --------------------

// a condition computes one value out of a graph of blocks, see ICondition.
// the id of a reference addresses an item of the list denoted below; null means "not set"
export enum ReferenceType {
  INPUT_VALUE = 0,
  CONDITION_VALUE = 1,
  ENERGY_METER_ENERGY = 2,
  TARIFF_CIRCUIT_POWER = 3,
  REGULATION_TARGET_VALUE = 4,
  OUTPUT_VALUE = 5,
  CONSTANT_DERIVED = 6, // see ReferenceConstant for the actual constant value
  MODULE_ERROR = 7,
  TARIFF_CIRCUIT_ACC_POWER = 8, // accumulated power of current period
  REGULATION_REL_ENERGY = 9, // relative acc. energy based on time of period and target value
}
// with ReferenceType.CONSTANT_DERIVED the reference id holds one of these values
export enum ReferenceConstant {
  ZERO = 0,
  DATE = 1, // YYMMDD
  TIME = 2, // s after midnight
  TIMESTAMP = 3, // minutes after 2015
  EPEX_PRICE = 4, // EPEX Spot energy prices from Awattar
  TEMPERATURE = 5, // weather forecast temperature
}
export enum ReferenceCompareType {
  GT = 0,
  GE = 1,
  LT = 2,
  LE = 3,
  EQ = 4,
  NE = 5,
  BITWISE_AND = 6,
}

// block types; all blocks are digital (boolean) except CONSTANT, VALUE, POWER, STATISTIC,
// WEATHER, PID, CURVE, EPEX (unless outputFormat COMPARE_DURATION), PRIORITY with
// ANALOG_* levels and FORMULA with a numeric result
export enum ConditionBlockType {
  FALSE = 0, // always false; also used for the pure comment block
  CONSTANT = 1, // fixed analog value
  CALENDAR = 2, // date ranges
  DAYTIME = 3, // time-of-day ranges, optionally relative to sunrise/sunset
  WEEKDAY = 4, // weekday and calendar week ranges
  PRIORITY = 5, // load management level of the regulation of the controlled output
  MINOFF = 6, // keeps the condition off for at least minOffTime
  MAXOFF = 7, // forces on if the condition was off for maxOffTime
  MINON = 8, // keeps the condition on for at least minOnTime
  PWM = 9, // pulses with onTime/offTime
  VALUE = 10, // value of the referenced item
  CONDITION = 11, // legacy, not implemented; use VALUE with a CONDITION_VALUE reference
  COMPARE = 12, // compares two references
  SEQUENCE = 13, // on/off pattern, rotated per regulation period
  ERROR = 14, // active alarms, see IConditionBlockAlarm
  POWER = 15, // switches off above the power limit of a tariff circuit
  EPEX = 16, // EPEX spot price evaluation
  STATISTIC = 17, // min/max/avg/sum/count over all references
  MAXON = 18, // forces off if the condition was on for maxOnTime
  WEATHER = 19, // sun hours or temperature of the weather forecast
  PID = 20, // pid controller
  TIMEREFERENCE = 21, // on within the time ranges given by reference pairs
  FORMULA = 22, // evaluates a mathjs expression over the referenced values
  CURVE = 23, // maps a reference through a piecewise linear function table
  START = 254, // implicit start node, never contained in ICondition.blocks
  END = 255, // implicit end node, never contained in ICondition.blocks
}

// block specific enums
// the level of a priority block is compared to the level of the regulation of the output
export enum ConditionBlockPriorityType {
  OFF = 0, // never on
  S1 = 1, // integral level 1..10 (S1..S10): on while the regulation stays below this level
  S10 = 10,
  T1 = 11, // trend level 1..10 (T1..T10): on once the regulation reached this trend level
  T10 = 20,
  ON = 21, // always on
  ANALOG_SX = 22, // analog: 1 - integralLevel / 10
  ANALOG_TX = 23, // analog: 1 - trendLevel / 10
}
export enum ConditionBlockTimeMode {
  CLOCK = 0, // time is absolute
  SUNRISE = 1, // time is relative to sunrise (needs general.latitude/longitude)
  SUNSET = 2, // time is relative to sunset
}
export enum EpexOutputFormat {
  ABSOLUTE_HOURS = 0, // time in period with EPEX price below limit
  RELATIVE_TIME = 1, // relative factor of time in period with EPEX price below limit
  RELATIVE_PRICE = 2, // relative factor of EPEX price below limit in period
  COMPARE_DURATION = 3, // whether the EPEX price is within the lower period of limit duration
}

export interface IConditionBlockBase {
  type: ConditionBlockType;
  invert: boolean; // inverts the result for digital blocks; analog blocks are negated
  active: boolean; // false disables the block, i.e. it is treated as not connected
  itsSettings?: string; // binary settings (legacy); absent on a full json parameter set
  position: number; // grid position for GUI, encoded (x << 4) | y
  isPersistent?: boolean; // keep (parts of) block memory across restarts
}

// blocks deriving values from other items; the roles of the entries are block specific
// and documented at the respective block below
export type IConditionBlockReference = IConditionBlockBase & {
  references: {
    type: ReferenceType;
    id: number | null; // reference might be undefined, which is indicated by id null
  }[];
};

// block type specific settings; the binary itsSettings blob is still used for compatibility,
// but these fields are the source of truth when present on read. they are optional
// because old parameter sets only carry the binary itsSettings blob.

export type IConditionBlockConstant = IConditionBlockBase & {
  value?: number; // constant analog value
};
// ranges are or'ed; a range with a start value above its end value wraps around
export type IConditionBlockCalendarRange = {
  startDay: number; // 0 = wildcard
  startMonth: number; // 0 = wildcard
  startYear: number; // 0 = wildcard
  endDay: number;
  endMonth: number;
  endYear: number;
};
export type IConditionBlockCalendar = IConditionBlockBase & {
  ranges?: IConditionBlockCalendarRange[];
};
export type IConditionBlockDaytimeRange = {
  startTime: number; // s after midnight (relative to mode)
  endTime: number; // s after midnight (relative to mode)
};
export type IConditionBlockDaytime = IConditionBlockBase & {
  ranges?: IConditionBlockDaytimeRange[];
  startMode?: ConditionBlockTimeMode;
  endMode?: ConditionBlockTimeMode;
};
export type IConditionBlockWeekdayRange = {
  startWeekday: number; // 1 = Mon ... 7 = Sun, 0 = wildcard
  startWeek: number; // calendar week 1..53, 0 = wildcard
  endWeekday: number;
  endWeek: number;
};
export type IConditionBlockWeekday = IConditionBlockBase & {
  ranges?: IConditionBlockWeekdayRange[];
};
export type IConditionBlockPriority = IConditionBlockReference & {
  // references: [0] single input/output/condition providing the level dynamically,
  // used instead of levels if levels is empty
  levels?: ConditionBlockPriorityType[]; // rotated one entry per period; empty selects the reference
};
export type IConditionBlockMinOff = IConditionBlockBase & {
  minOffTime?: number; // s
};
export type IConditionBlockMaxOff = IConditionBlockBase & {
  maxOffTime: number; // s
  // s, on-time forced after maxOffTime; null switches to the alternative mode, which is on
  // during the last maxOffTime seconds of the regulation period
  onTime: number | null;
};
export type IConditionBlockMinOn = IConditionBlockBase & {
  minOnTime?: number; // s
};
export type IConditionBlockMaxOn = IConditionBlockBase & {
  maxOnTime: number; // s
  offTime: number | null; // s, off-time forced afterwards; null: rest of the regulation period
};
export type IConditionBlockPwm = IConditionBlockReference & {
  // references: single entry, the optional reset trigger
  onTime?: number; // s
  offTime?: number; // s
  pulseCount?: number; // 0 = continuous mode
  idleState?: number; // 0 = off, 1 = on
};
export type IConditionBlockValue = IConditionBlockReference & {
  // references: [0] the item providing the value
  offset?: number;
  isReciprocal?: boolean; // 1/x
};
export type IConditionBlockCompare = IConditionBlockReference & {
  // references: [0] compared against [1], i.e. ref[0] <operator> ref[1] + offset
  offset?: number;
  hysteresis?: number; // applied while the block is on
  onDelay?: number; // s, monitor period for switch on
  offDelay?: number; // s, monitor period for switch off
  compareOperator?: ReferenceCompareType;
};
export type IConditionBlockSequence = IConditionBlockBase & {
  periods?: boolean[]; // output state per period, rotated one entry per period
};
export type IConditionBlockAlarm = IConditionBlockReference & {
  // references: [0] optional filter on the item the alarm relates to, e.g. the module id;
  // ignored for the alarm sources without a related item (restart, custom)
  alarmSource: ControllerAlarmSource | undefined; // undefined matches any alarm
};
export type IConditionBlockPower = IConditionBlockReference & {
  // references: [0] the tariff circuit whose power is observed (TARIFF_CIRCUIT_POWER)
  power?: number; // kW, trigger limit to switch off
  reactivationPower?: number; // kW, trigger limit to switch on
  onDelay?: number; // s, monitor period for switch on
  offDelay?: number; // s, monitor period for switch off
};
export type IConditionBlockEpex = IConditionBlockReference & {
  // references:
  // [0] period duration [h] or, for a fixed period, period end [s after midnight]
  // [1] limit value or required duration [h] for COMPARE_DURATION
  // [2] optional period start [s after midnight]; its presence denotes a fixed period,
  //     which spans midnight if the end time is earlier than the start time
  observationOffset?: number; // offset to reference [0]
  limitOffset?: number; // offset to reference [1]
  periodStartOffset?: number; // offset to reference [2]
  outputFormat?: EpexOutputFormat;
};
export type IConditionBlockStatistics = IConditionBlockReference & {
  // references: the set of values to evaluate, any number of entries
  operation: "min" | "max" | "avg" | "sum" | "count_greater" | "count_smaller";
  limit: number; // compare value of the count operations
};
export type IConditionBlockWeather = IConditionBlockReference & {
  // references: [0] observation period [h] starting now
  value: "sunhours" | "temperature"; // sum of sun hours resp. average temperature
  observationOffset: number; // h, offset to reference [0]
};
export type IConditionBlockPid = IConditionBlockReference & {
  // references: [0] process variable, [1] setpoint, [2] optional reset trigger
  kp: number;
  ki: number;
  kd: number;
  deadband?: number; // optional deadband, where no control action is taken
  outputMin: number | undefined;
  outputMax: number | undefined;
  setpointOffset?: number; // optional offset added to the setpoint, i.e. for constant zero
};
export type IConditionBlockCurve = IConditionBlockReference & {
  // references: [0] input value
  // table of the function, sorted by x; the output is linearly interpolated between
  // neighbouring points and clamped to the first/last y outside the table range
  points: { x: number; y: number }[];
};
export type IConditionBlockTimeReference = IConditionBlockReference & {
  // references: pairs of [2i] start time and [2i+1] end time, both s after midnight;
  // the block is on within any of these ranges
  offsets: number[]; // s, added to the reference of the same index
};
export type IConditionBlockFormula = IConditionBlockReference & {
  // references: the variables of the expression, references[i] is named variables[i]
  variables: string[];
  // mathjs expression, may consist of several statements separated by ";" or newlines, the
  // last one is the result; "#" starts a comment up to the end of the line;
  // a boolean result makes the block digital, a number analog, anything else yields undefined;
  // user defined functions (f(x) = ...) are not allowed;
  // enplan shows the first line as the block's text, thus start with a short "#" comment
  // (up to about 13 characters) naming the result;
  // besides the variables and the mathjs operators and functions it may use:
  //   isFirstRun    true on the first evaluation, i.e. after a restart or parameter change
  //   lastResult    result of the previous evaluation; undefined on the first evaluation,
  //                 unless restored by persistence (isPersistent)
  //   deltaTime     s since the previous evaluation, 0 on the first evaluation
  //   x ?? fallback, x != undefined   handling of undefined values, e.g. of offline modules
  //   lastValue(x)  value of variable x at the previous evaluation, undefined at first
  //   rise(x), fall(x)   edge of variable x since the previous evaluation, false at first
  //   lim(x, lo, hi)     x clamped to [lo, hi]
  //   curve(x, xs, ys)   deprecated, use the CURVE block or lim(); piecewise linear table
  //   log("label", x)    debug log of x, returns x
  //   inputs("pattern"), outputs(..), tariffCircuits(..)
  //       array of the defined values of all items whose name matches the pattern (wildcards
  //       * and ?); used with array functions like sum, mean, min, max, count and element-wise
  //       operators, e.g. sum(inputs("WR * | Regelbar") > 0)
  expression: string;
};
// type FALSE; not part of the signal flow, just a free-text note that may contain html
export type IConditionBlockComment = IConditionBlockBase & {
  comment?: string;
};

export type IConditionBlock =
  | IConditionBlockBase
  | IConditionBlockComment
  | IConditionBlockMaxOff
  | IConditionBlockMaxOn
  | IConditionBlockStatistics
  | IConditionBlockWeather
  | IConditionBlockPid
  | IConditionBlockCurve
  | IConditionBlockFormula
  | IConditionBlockConstant
  | IConditionBlockPwm
  | IConditionBlockPriority
  | IConditionBlockMinOff
  | IConditionBlockMinOn
  | IConditionBlockSequence
  | IConditionBlockCalendar
  | IConditionBlockDaytime
  | IConditionBlockWeekday
  | IConditionBlockValue
  | IConditionBlockCompare
  | IConditionBlockPower
  | IConditionBlockEpex;

// a condition is a directed graph of blocks between an implicit start and end node.
// its result is true/non-zero if a path from start to end exists on which all blocks are
// true/non-zero: blocks in series are and'ed (analog: multiplied), parallel paths are
// or'ed (analog: added). the condition is analog as soon as one of its blocks is analog.
export type ICondition = IParameterItem & {
  // node order is [start, end, ...blocks]; index 0/1 are the implicit start/end blocks
  // adjacencyMatrix[from][to] = true denotes a directed edge; the matrix is quadratic
  // with blocks.length + 2 rows
  adjacencyMatrix: boolean[][];
  blocks: IConditionBlock[]; // user blocks only (start/end excluded)
  onDelay: number; // s, delays the switch to a true/non-zero result
  offDelay: number; // s, delays the switch to a false/zero result
  // grid positions of the implicit start and end block, encoded (x << 4) | y; enplan places them
  // at 0x04 and 0x64 for a new condition, i.e. (0,4) and (6,4): a single path lies in between on
  // row 4, parallel paths on the rows around it, comment blocks above (e.g. row 1)
  startPos: number;
  endPos: number;
};

// -------------------- energy meters --------------------

export enum EnergyUnit {
  KWH = "kWh",
  NONE = "",
  QM = "m³",
  L = "l",
  KG = "kg",
  KVARH = "kvarh",
}

export type EnergySource = "electrical" | "gas" | "water" | string;

export type IEnergyMeter = IParameterItem &
  IAddress & {
    impulseConstant: number; // pulses per unit, i.e. energy = pulse count / impulseConstant
    converterFactor: number; // Wandlerfaktor, multiplied onto energy and power
    number: string; // meter number, for documentation only
    energySource?: EnergySource; // since v7.4-506
    unit: EnergyUnit;
    // derived power of pulse count modules is zeroed if the next pulse is that overdue
    zeroPowerLimit?: number; // % of last power, 0 to disable, default 10 %
    // replacement power while the energy meter is offline; one of:
    fallback?: {
      power?: number; // fixed power, in kW
      em?: number; // id of an energy meter providing the power
      tc?: number; // id of a tariff circuit providing the power
    };
    // obsolete members
    charger?: number | null; // removed in v7.6-464 in favor of IAddress.module
    factorI: number; // removed in v25.04, use converterFactor instead
    factorU?: number; // removed in v25.04, use converterFactor instead
    correction?: number; // %, removed in v25.04, use converterFactor instead
  };

// -------------------- tariff circuits --------------------

export enum PowerUnit {
  KWH = "kW",
  NONE = "1/h",
  QM = "m³/h",
  L = "l/h",
  KG = "kg/h",
  KVARH = "kvar",
}

// this cost tariff enum is used within the trend-DB only
export enum CostTariff {
  SummerHT = 0,
  SummerLT = 1,
  WinterHT = 2,
  WinterLT = 4,
  Special = 5,
}

// a tariff circuit sums up energy meters to one measured power, accumulated over the
// regulation period; it is the measured value of a regulation
export type ITariffCircuit = IParameterItem & {
  unit: PowerUnit;
  regulationPeriod: number; // s, billing period of the utility, typically 900
  costTariff: number | null; // specific cost tariff; implies CostTariff.Special in trending DB then
  parent?: number | null; // since v7.4-507; id of the superordinate tariff circuit
  categories?: string[]; // since v7.4-509

  // contributing energy meters or other tariff circuits
  contributions: {
    em?: number; // id of the energy meter
    tc?: number; // id of the contained tariff circuit, alternative to em
    factor: number; // weight, e.g. -1 to subtract a feed-in meter
  }[];

  // synchronisation of the period start with the utility
  syncSource: number | null; // id of the input providing the sync pulse, null for internal clock sync
  syncTimeout: number; // s, free run tolerance before the sync alarm is raised
  syncSuspension: number; // s, sync pulses are ignored for this time after a period start
};

// -------------------- regulation --------------------

export enum TargetValueOptimizationFlag {
  NONE = 0, // normal optimization run
  LOCKED_1_TV_EXCEEDED = 1, // target value exceeded during optimization period
  LOCKED_2_OPT_TV_EXCEEDED = 2, // optimized target value exceeded during optimization period
  LOCKED_3_OPT_MAX_REACHED = 3, // maximum optimization reached
}

// a regulation keeps the power of its tariff circuit below the target value by switching
// off the assigned outputs, which is driven by priority blocks in their conditions
export type IRegulation = IParameterItem & {
  tariffline: number | null; // id of the observed tariff circuit
  defaultTargetValue: number; // kW, used if no targetValueCondition is set
  targetValueCondition: number | null; // id of a condition providing the target value
  targetValueOptimization: boolean; // automatically lower the target value, see IGeneral
};

// -------------------- chargers --------------------

// legacy, used in enline <= 7.6
export enum ChargerTypeNum {
  OTHER = 0,
  BENDER = 1,
  GOE_HOME = 2,
  PCE_GL = 3,
  KEBA = 4,
  ETREL = 5,
  CIRCTRL = 6,
  OCPP = 7,
  SALIA = 8,
  ALFEN = 9,
  COMPLEO_EBOX = 10,
  COMPLEO_DC = 11,
  KOSTAD = 12,
  HYPERCHARGER = 13,
  ZAPTEC = 14,
  ENERCHARGE = 15,
  COMPLEO_SOLO = 16,
  MENNEKES = 17,
  SIEMENS = 18,
  GREENMOTION = 19,
  GARO_ENTITY = 20,
  SATURN22 = 21,
  EASEE = 22,
}

export type ICharger = IParameterItem & {
  // actually ChargerType from chargerdefs.ts, but cannot use here due to import;
  // the type ids and the legacy ChargerTypeNum mapping are listed in modules/enplanconfig.json
  type: ChargerTypeNum | string;
  // "host", "host:port", "https://host/A" (trailing /A..: connector) or, for modbus,
  // "unitId@host" resp. a plain rtu address like "3" or "3@ext1:19200/8E1"
  ipAddress: string;
  pollInterval: number; // s
  firstPhase?: number; // enline > 6.2; 1..3, rotates the phase readings of the charger
  settings?: Record<string, string | number | boolean>; // enline >= v7.6-463, per type
  password?: string; // deprecated, now part of settings struct

  // load management; the limits are ids of conditions providing the requested maximum
  dpoReqMaxPower: number | null; // id of the condition yielding the power limit [kW]
  dpoReqMaxCurrent: number | null; // id of the condition yielding the current limit [A]
  regulation?: number | null; // enline >= 7.5; id of the regulation used as their context

  // backend forwarding
  backend?: string; // enline >= 6.3.2
  ignoreBackendErrors?: boolean; // enline >= 6.3.2
};

// -------------------- modules --------------------

export type IModule = IParameterItem & {
  // actually ModuleType from moduledefs.ts, but cannot use here due to import;
  // the type ids are listed in modules/enplanconfig.json
  type: string;
  address: string; // same format as ICharger.ipAddress
  pollInterval: number; // s
  template?: ITemplate | null; // only for the generic modbus module types
  password?: string; // deprecated, now part of settings struct
  settings?: Record<string, string | number | boolean>; // since v7.6-203, per type
};

// generic modbus device description
export interface ITemplate {
  name: string;
  creator?: string;
  version?: string;
  device?: {
    vendor: string;
    model: string;
  };
  maxBulkRegisters?: number; // maximum number of registers to read with a single request
  timeout?: number; // ms, overwrite default modbus communication timeout
  continueOnError?: boolean; // continue reading other channels if some fails
  errorlimit?: number; // overwrite communication error limit for alarm
  // the array index of a channel is the channel number used by IAddress.channel
  channels: (ITemplateChannelInput | ITemplateChannelOutput | ITemplateChannelEnergymeter)[];
}
export interface ITemplateChannel {
  name: string;
}
export interface ITemplateChannelInput extends ITemplateChannel {
  type: "input";
  value: ITemplateValue;
}
export interface ITemplateChannelOutput extends ITemplateChannel {
  type: "output";
  value: ITemplateValue;
  writeAlways?: boolean; // write value even if not changed
}
export interface ITemplateChannelEnergymeter extends ITemplateChannel {
  type: "energymeter";
  energy: ITemplateValue;
  power?: ITemplateValue;
}
export interface ITemplateValue {
  /*
  register: type:addr:datatype:endian
  type: RH3 = read holding register (03)
  addr: register
  datatype: I8, U8, I16, U16, I32, U32, F, D
  endian: BE (big endian), LE (little endian), ME (middle endian), SE (swap endian)
  */
  register: string; // "RH3:0:I32:BE"
  // allows to overwrite the address of a specific channel to query multiple modules with the same template
  address?: number;
  // allows scaling of the value
  offset?: number;
  factor?: number;
  onlyPositive?: boolean; // negative values are cut to zero
}

// -------------------- sequences --------------------

export enum SequenceCmd {
  OFF = 0,
  ON = 1,
  TOGGLE = 2,
}

export interface ISequenceEntry {
  delay: number; // s, waited before this step, relative to the previous one
  output: number; // id of the output to set
  state: SequenceCmd;
}

// a sequence switches outputs one after the other, triggered by an edge of its condition
export type ISequence = IParameterItem & {
  mode: OutputMode; // trigger edge; only PULSE_ON, PULSE_OFF and PULSE_BOTH are used
  condition: number | null; // id of the triggering condition
  entries: ISequenceEntry[];
};

// -------------------- alarms --------------------

// alarms triggered by the controller
export enum ControllerAlarmSource {
  TargetValue = "NVAlarm_Targetvalue",
  Restart = "NVAlarm_Restart",
  MissingSync = "NVAlarm_MissingSync", // allows clearing
  ManualActive = "NVAlarm_ManualActive", // allows clearing
  EnergyMeter = "NVAlarm_EnergyMeter",
  // only used in strato device
  Modul = "NVAlarm_Modul", // allows clearing
  Custom = "NVAlarm_Custom", // allows clearing
  // only used in Loytec device
  Linx = "NVAlarm_LINX",
}

// alarm value of restart alarms
export enum RestartType {
  Unexpected = 0, // also unknown reason and legacy devices
  Update = 1, // SW/OS update or installation
  Manual = 2, // reboot triggered by user
  Automatic = 3, // daily automatic restart
}

// bits of IGeneral.enabledAlarms
export enum AlarmEnableFlag {
  Restart = 0x0001,
  MissingSync = 0x0002,
  Simulation = 0x0004, // obsolete
  TargetValue = 0x0008,
  ManualActive = 0x0010,
  Modul = 0x0020,
  EnergyMeter = 0x0040,
  Internal = 0x0080, // obsolete
  Linx = 0x0100,
}

// -------------------- general settings --------------------

export type IGeneral = {
  // site
  site: string; // site description string; not site name
  longitude?: number; // introduced in enline 7.4; needed for sunrise/sunset and weather
  latitude?: number; // introduced in enline 7.4

  // control cycle
  cycleTime?: number; // ms, main control cycle time
  intIntersection: number; // relative point of the period where the on/off limits meet, e.g. 0.95
  maxLoopDelay: number; // s, minimum time between two integral regulation steps
  autoRestart?: boolean; // introduced in enline 8.0.1

  // load management: fractions of the target value defining when the integral regulation
  // switches to the next/previous level; one entry per level, index 0 = level 0
  activationPoints: number[]; // limits to release loads again
  deactivationPoints: number[]; // limits to switch loads off

  // alarms
  enabledAlarms: number; // bit mask of AlarmEnableFlag
  // alarms of these items are not raised, value is the item id (e.g. module id) or the RestartType
  suppressedAlarms?: { source: ControllerAlarmSource; value: number }[];
  alarmTargetValueOvershoot?: number; // %, the overshoot which is still tolerated before alarm
  customAlarm?: number | null; // introduced in v26.1; id of a condition raising a custom alarm

  // cost tariffs; the first entry whose condition is true defines the current cost
  costTariffs: { cost: number; condition: number | null }[];
  defaultCostTariff: number; // used if no cost tariff condition matches
  peakCostTariff: number; // cost per kW of the yearly peak power

  // target value optimization, applies to regulations with targetValueOptimization set
  maxOptimization: number; // lower limit of the optimized target value, relative to the nominal one
  // classic optimization using table based on free power
  // the first entry whose energy exceeds the observed relative free power reduces the
  // target value by its factor
  optimizationPoints: { energy: number; factor: number }[];
  // alternative optimization based on median day values
  optimizationDayMaximaFactor?: number; // if set: target value = factor * median of day maxima
  optimizationCondition: number | null; // id of a condition enabling the optimization
  optimizationPeriod: number; // days

  // trending
  trendCalcCycle: number; // s, cycle of the trend regulation
  trendLevelOffDelay: number; // s
  trendLevelOnDelay: number; // s
  trendOutputOnDelay: number; // s, minimum time between two trend switch-on events

  // modbus server, providing the items of this parameter set to other systems
  modbusTcpEnable?: boolean; // introduced in v8.0-91
  modbusRtuEnable?: boolean; // introduced in v8.0-91
  modbusRtuSettings?: string; // introduced in v8.0-91; e.g. "19200/8E1"
  modbusEnableInputWrite?: boolean; // introduced in v8.0-91; allow writing virtual inputs

  // cloud
  cloudEnable?: boolean;
  cloudVm?: string[]; // cloud-vm machine ids to send live data to

  // mqtt
  mqttEnable: boolean;
  mqttHost: string;
  mqttUser: string;
  mqttPassword: string;
  mqttPrefix: string;
  mqttRetain: boolean;

  customSettings?: ICustomSettings; // since v25.05
};

export interface ICustomSettings {
  customDashboard?: CustomDashboard;
  customTariff?: ICustomTariff;
}

// ids of the inputs resp. outputs providing these dashboard values
export interface CustomDashboardValues {
  tempOut?: number;
  tempIn?: number;
  savings?: number; // e.g. to show CO2 savings
  socBattery?: number;
  socCustom1?: number;
}
export interface CustomDashboard {
  // page header
  title?: string;
  preTitle?: string;
  logo?: string; // image of logo, e.g. "data:image/svg+xml;base64,..."
  sitetext1?: string;
  sitetext2?: string;

  // energy flow tiles, each holding the ids of the contributing tariff circuits
  tariffCircuits: {
    gridImport?: number[];
    gridExport?: number[];
    battery?: number[];
    pv?: number[];
    home?: number[];
    custom1?: number[]; // e-mobility
    custom2?: number[];
    custom3?: number[]; // other
  };
  custom3Name?: string; // overwrite name for "other" tile
  nominalPower: number; // used to normalize the animation speed

  // graph
  timerange?: number; // days, time to use for graph and energy data
  graph?: {
    type?: "line" | "bar";
    aggregation?: "HOUR" | "DAY" | "MONTH" | "NONE";
    hideCustom1?: boolean;
    hideCustom2?: boolean;
    hideCustom3?: boolean;
    separateGridImport?: boolean; // show grid import/export in separate graph
    showAllPositive?: boolean; // show negative values as positive graphs
  };

  // special values can be obtained from inputs or outputs
  inputs?: CustomDashboardValues;
  outputs?: CustomDashboardValues;
}

// this allows using custom API for getting variable cost tariffs, which defaults to EPEX
export interface ICustomTariff {
  format: "EKZ" | "CKW" | "TVPP";
  url?: string; // optional, otherwises uses default URLs of services
  // CKW tariffName: "home_dynamic" | "business_dynamic" (default: "business_dynamic")
  tariffName?: string;
  // CKW tariffType: "electricity" | "grid_usage" | "grid" | "integrated" (default: "integrated")
  tariffType?: string;
  // TVPP: OAuth client credentials for the e-Steiermark login endpoint
  clientId?: string;
  clientSecret?: string;
}

// -------------------- parameter set --------------------

// definition of parameter set
// note: real parameter sets may hold additional legacy members not listed here; keep them
// unchanged instead of dropping them when modifying a parameter set
export interface IParameter {
  comment?: string;

  general: IGeneral;
  input: IInput[];
  condition: ICondition[];
  energymeter: IEnergyMeter[];
  tariffline: ITariffCircuit[]; // the tariff circuits
  regulation: IRegulation[];
  output: IOutput[];
  charger: ICharger[];
  module: IModule[];
  sequence?: ISequence[]; // since 7.4-551
}

// extended parameters, as used in the LINX/embyt controller;
// this is also the content of an .epara file, see below
export type ILinxParameter = IParameter & {
  siteName: string;
};

// -------------------- utility functions --------------------

export function getBusNr(bus: Bus) {
  if (bus === Bus.LC) return 0;
  if (bus === Bus.FT) return 1;
  if (bus === Bus.MOD) return 2;
  if (bus === Bus.M) return 3;
  if (bus === Bus.IP) return 4;
  if (bus === Bus.LOCAL) return 5;
  if (bus === Bus.VIRTUAL) return 6;
  if (bus === Bus.CHARGER) return 7;
  if (bus === Bus.MODULE) return 8;
  // should never happen
  return 8;
}

export function fromBusNr(bus: number) {
  if (bus === 0) return Bus.LC;
  if (bus === 1) return Bus.FT;
  if (bus === 2) return Bus.MOD;
  if (bus === 3) return Bus.M;
  if (bus === 4) return Bus.IP;
  if (bus === 5) return Bus.LOCAL;
  if (bus === 6) return Bus.VIRTUAL;
  if (bus === 7) return Bus.CHARGER;
  if (bus === 8) return Bus.MODULE;
  // should never happen
  return Bus.MODULE;
}
